import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import LineChart from '@components/chart/PercentLineChart'
import Header from '@components/header/Header'
import ConformityCard from '@components/card/RewardConformityCard'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import ChartTag from '@components/chart/ChartTag'
import SortSelector from '@components/modal/SortSelector'
import ButtonSelect from '@components/select/ButtonSelect'

import useReducer from '@store/useReducer'
import * as LocationStore from '@store/location/location'
import * as SiteStore from '@store/site/site'

import api from '@api/api'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import { format, subYears } from 'date-fns'
import { MODES, getAxis, findAxisIndex, findAxisLabels } from './utils'

import Logger from '@utils/logger'

interface Props {
  espaceId: string
}

const Space = ({ espaceId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [{ width }, bp] = useBreakpoint()

  const site = useReducer(SiteStore.store, (s) => s.site)

  const places = useReducer(LocationStore.store, (s) => s.places)
  const space = React.useMemo(() => places.find((p) => p.id === espaceId), [places, espaceId])

  const [stats, setStats] = React.useState<ControleQualiteStatistiqueResponse>()
  const [histo, setHisto] = React.useState<ControleQualiteHistoriqueResponse[]>([])
  const [previousHisto, setPreviousHisto] = React.useState<ControleQualiteHistoriqueResponse[]>([])

  const [mode, setMode] = React.useState<'all' | 'year' | 'halfYear' | 'month'>('halfYear')

  const axis = React.useMemo(() => getAxis(mode, [...histo, ...previousHisto]), [mode, histo, previousHisto])

  const labels = React.useMemo(() => findAxisLabels(mode, axis, i18n), [axis])

  const data = React.useMemo(
    () => [
      {
        items: previousHisto
          .map((h) => ({ value: h.tauxConformiteMoyen, index: findAxisIndex(mode, new Date(h.date), axis) }))
          .filter((item) => item.index >= 0),
        color: theme.colors.disable,
      },
      {
        items: histo
          .map((h) => ({ value: h.tauxConformiteMoyen, index: findAxisIndex(mode, new Date(h.date), axis) }))
          .filter((item) => item.index >= 0),
        color: theme.colors.primary,
      },
    ],
    [histo, previousHisto, axis]
  )

  const params = MODES[mode]

  React.useEffect(() => {
    if (site) {
      // space stats
      api.controle
        .stats({ espaceId, siteId: site.id })
        .then(setStats)
        .catch((err) => {
          setStats(undefined)
          Logger.error(err)
        })

      // current period scores
      api.controle
        .historique({
          dateDebut: format(params.dateDebut, 'yyyy-MM-dd'),
          dateFin: format(params.dateFin, 'yyyy-MM-dd'),
          intervalle: params.intervalle,
          espaceId,
          siteId: site.id,
        })
        .then((list) => setHisto(list.historique))
        .catch((err) => {
          setHisto([])
          Logger.error(err)
        })

      // previous period scores
      api.controle
        .historique({
          dateDebut: format(subYears(params.dateDebut, 1), 'yyyy-MM-dd'),
          dateFin: format(subYears(params.dateFin, 1), 'yyyy-MM-dd'),
          intervalle: params.intervalle,
          espaceId,
          siteId: site.id,
        })
        .then((list) => setPreviousHisto(list.historique))
        .catch((err) => {
          setPreviousHisto([])
          Logger.error(err)
        })
    }
  }, [params, site])

  if (!space) {
    return (
      <Root header={i18n.t('screens.controles.title')} centered onLeft={navigation.goBack}>
        <NoContent>{i18n.t('screens.controles.form.spaces.empty')}</NoContent>
      </Root>
    )
  }

  const headerLinks = [
    { label: i18n.t('screens.controles.title'), onClick: () => navigation.jumpTo('/controles') },
    { label: space.libelle, onClick: () => null },
  ]

  const historyButton: HeaderButton = {
    label: i18n.t('screens.controleDetail.history'),
    onClick: () => navigation.push(`/controles/history/list?espaceId=${espaceId}`),
    color: 'light',
    status: stats && stats.nombreControle > 0 ? 'active' : 'disabled',
  }

  const options = Object.keys(MODES).map((key) => ({ label: i18n.t(`screens.controleDetail.sort.${key}`), value: key }))
  const option = options.find((opt) => opt.value === mode)

  const renderInfos = () => {
    if (!stats || !space) {
      return null
    }

    if (bp === 'phone') {
      return (
        <InfosContainer>
          <InfoLightTitleContainer>
            <Name>{stats.nombreControle}</Name>
            <InfoLight>{i18n.t('screens.controleDetail.count', { count: stats.nombreControle })}</InfoLight>
          </InfoLightTitleContainer>

          <InfoLightContainer>
            <InfoLight>{`${stats.nombreSocietesAudite} ${i18n.t('screens.controleDetail.societe', {
              count: stats.nombreSocietesAudite,
            })}`}</InfoLight>
          </InfoLightContainer>
          <InfoLightContainer>
            <InfoLight>{`${stats.nombreControleAvecObservation} ${i18n.t(
              'screens.controleDetail.observation'
            )}`}</InfoLight>
          </InfoLightContainer>

          <RewardContainer>
            <ConformityCard score={stats.tauxConformiteMoyen} />
          </RewardContainer>
        </InfosContainer>
      )
    }

    return (
      <InfosContainer>
        <TotalContainer>
          <TotalCount>{stats.nombreControle}</TotalCount>
          <Total>{i18n.t('screens.controleDetail.count', { count: stats.nombreControle })}</Total>
        </TotalContainer>

        <Infos>
          <InfoCard>
            <InfoCount>{stats.nombreSocietesAudite}</InfoCount>
            <Info>{i18n.t('screens.controleDetail.societe', { count: stats.nombreSocietesAudite })}</Info>
          </InfoCard>
          <InfoCard>
            <InfoCount>{stats.nombreControleAvecObservation}</InfoCount>
            <Info>{i18n.t('screens.controleDetail.observation')}</Info>
          </InfoCard>
        </Infos>

        <ConformityCard score={stats.tauxConformiteMoyen} />
      </InfosContainer>
    )
  }

  return (
    <Root
      header={space.libelle}
      onLeft={navigation.goBack}
      renderSubHeader={renderInfos}
      contentContainerStyle={{ flexGrow: 1 }}>
      {bp !== 'phone' && (
        <Header links={headerLinks} title={space.libelle} buttons={[historyButton]} renderBottom={renderInfos} />
      )}

      <ContentContainer>
        <SectionTitle title={i18n.t('screens.controleDetail.graph')} />

        {bp === 'phone' && (
          <Sort>
            <SortSelector
              sorts={options}
              sort={option}
              reverse
              onSortChange={(opt) => {
                if (opt) {
                  setMode(opt.value)
                }
              }}
            />
          </Sort>
        )}

        <ChartLine>
          <TagContainer>
            <ChartTag label={space.libelle} color={theme.colors.primaryLight} textColor={theme.colors.primary} />
          </TagContainer>
          <TagContainer>
            <ChartTag
              label={i18n.t('screens.controleDetail.previous')}
              color={theme.colors.backgroundSecondary}
              textColor={theme.colors.disable}
            />
          </TagContainer>

          {bp !== 'phone' && (
            <Sort>
              <SortSelector
                sorts={options}
                sort={option}
                reverse
                onSortChange={(opt) => {
                  if (opt) {
                    setMode(opt.value)
                  }
                }}
              />
            </Sort>
          )}
        </ChartLine>

        <ChartContainer>
          {histo.length === 0 ? (
            <Empty>
              <EmptyText>{i18n.t('screens.controleDetail.empty')}</EmptyText>
            </Empty>
          ) : (
            <LineChart key={width} labels={labels} data={data} size={bp === 'phone' ? 'small' : 'large'} />
          )}
        </ChartContainer>
      </ContentContainer>

      {bp === 'phone' && (
        <Buttons>
          <ButtonSelect label={historyButton.label} onClick={historyButton.onClick} />
        </Buttons>
      )}
    </Root>
  )
}

export default Space

const InfosContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-direction: row'};
  padding: 32px 0;
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 0px; padding-top: 24px'};
  flex-wrap: wrap;
`

const TotalCount = styled(Text)`
  ${(props) => props.theme.fonts.Heading1};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Total = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  color: ${(props) => props.theme.colors.secondaryText};
`

const TotalContainer = styled(View)`
  margin-right: 24px;
`

const Infos = styled(View)`
  flex-flow: row wrap;
  flex: 1;
  min-width: 100px;
`

const InfoCard = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.cardSecondary};
  padding: 16px;
  align-items: center;
  flex: 1;
  margin: 0 8px;
  max-width: 250px;
  min-width: 100px;
`

const InfoCount = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
  font-size: 24px;
  line-height: 28px;
  margin-bottom: 8px;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const ContentContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex: 1'};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 32)}px;
`

const ChartContainer = styled(View)`
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.background : props.theme.colors.cardSecondary};
  padding: 5px;
  border-radius: 8px;
  ${(props) => (props.theme.breakpoint === 'phone' ? 'height: 200px' : 'flex: 1; min-height: 400px')};
`

const ChartLine = styled(View)`
  flex-direction: row;
  align-items: center;
  margin: 16px 0;
`

const TagContainer = styled(View)`
  margin-right: 16px;
`

const Sort = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'position: absolute; right: 16px; top: 18px;' : 'flex: 1')};
  align-items: flex-end;
`

const Name = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
  color: ${(props) => props.theme.colors.linkDisabled};
`

const InfoLight = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.background};
`

const InfoLightContainer = styled(View)`
  background-color: ${(props) => `${props.theme.colors.cardSecondary}22`};
  padding: 8px;
  margin-bottom: 1px;
`

const RewardContainer = styled(View)`
  position: absolute;
  bottom: 0;
  right: 0;
`

const InfoLightTitleContainer = styled(View)`
  margin-bottom: 16px;
`

const Buttons = styled(View)`
  padding: 16px 0;
`

const NoContent = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  text-align: center;
  margin: 24px 0;
`

const Empty = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`

const EmptyText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
  margin: 0 24px;
  margin-bottom: 50px;
`
